//src/pages/AddPatientModal/OutPatientAppointmentModal.tsx :-
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box, 
  Typography,
  TextField,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Alert,
  List,
  ListItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import * as Yup from "yup";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import { format } from "date-fns";
import {
  getDoctorByDepartmentId,
  getDoctorsByDepartmentIdMultiple,
  getDoctorById,
  getAllDoctors,
} from "../../services/doctorService";
import { getAllStaff } from "../../services/StaffService";
import { registerPatient, updatePatient, getAllPatients, syncPatientTypeForWorkflow } from "../../services/patientService";
import { createAppointment, getAppointmentsByDoctorAndDate, generateTokenForAppointment, getAppointmentsByPatientId, updateAppointment } from "../../services/appointmentService";
import { scheduleRemindersForAppointment, rescheduleAppointmentReminders } from "../../services/appointmentReminderService";
import { createInvoice, getInvoicesByPatientId } from "../../services/invoiceService";
import { saveAssignment } from "../../services/PatientAssignmentService";
import { create } from "../../services/PatientAdmissionService";
import dayjs from "dayjs";
import { getAllDepartment } from "../../services/departmentService";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AppointmentDTO } from '../../types/AppointmentDTO'; // Adjust the path as necessary
import { getAllWards } from "../../services/WardService";
import { getBedsByWard } from "../../services/BedService";
import {
  InvoiceAttachmentControl,
  InvoiceSummarySection,
  calcInvoiceGstBreakdown,
  resolveAttachmentUrl,
  formatInvoiceInr,
} from "../../components/InvoiceFormShared";
import { useOrganizationGstPercentage } from "../../hooks/useOrganizationGstPercentage";
import { useOpdConsultationWaiverSettings } from "../../hooks/useOpdConsultationWaiverSettings";
import { getOrganizationId, getOrganizationName, fetchOrganizationProfile } from "../../utils/organizationUtils";
import { useAuth } from "../../context/AuthContext";
import getAllTokens from "../../services/TokenService";
import { shouldAutoGenerateToken, extractTokenNumber, isAppointmentCompleted, isBlockingSameDayAppointment } from "../../utils/appointmentCancelUtils";
import { filterDepartmentsForOpdWorkflow } from "../../utils/subscriptionScopeUtils";
import { rememberInvoiceDepartment } from "../../utils/invoiceDepartmentMemory";
import { notifyOpdPatientsRefresh } from "../../utils/opdPatientsRefresh";
import PatientRegistrationSuccessStep from "../../components/PatientRegistrationSuccessStep";
import { uploadAttachment } from "../../services/uploadService";
import { getLatestOpdConsultationWaiverAnchorDate } from "../../utils/patientPeriodSummary";
import { requiresConsultationPaymentAfterVisitGap } from "../../utils/opdConsultationWaiverUtils";
import {
  createAppointmentAmountValidationSchema,
  getAppointmentInvoiceTypeOptions,
  isAppointmentInvoiceSubmitDisabled,
  isFirstOpdBillingVisit,
  validateAppointmentInvoiceAmount,
} from "../../utils/appointmentInvoiceTypeUtils";
import { resolveAppointmentId, buildReschedulePayloadForSingleAppointment } from "../../utils/appointmentBookingUtils";
import {
  buildOpdServiceInvoiceDocumentHtml,
  printPatientSummaryDocument,
} from "../../utils/patientSummaryDocumentUtils";
import {
  LARGE_FORM_MODAL_SX,
  SUCCESS_STEP_MODAL_SX,
  FORM_CONFIRM_DETAILS_BOX_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
  DENSE_FIELD_PROPS,
} from "../../utils/formModalStyles";

const baseAppointmentValidationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name should be of minimum 2 characters length"),
  gender: Yup.string().required("Gender is required"),
  dob: Yup.date()
    .required("DOB is required")
    .max(dayjs(), "DOB cannot be in the future"), 
  address: Yup.string().required("Address is required"),
  patient_type: Yup.string().required("patient type is required"),
  aadhar: Yup.string()
    .notRequired() // Make Aadhar optional
    .test('aadhar-format', 'Aadhar should be a 12-digit number', function(value) {
      if (!value || value.length === 0) return true; // Allow empty values
      return /^\d{12}$/.test(value);
    }),
  email: Yup.string()
    .email("Enter a valid email")
    .notRequired(), // Make email optional
  mobile: Yup.string()
    .notRequired() // Make contact number optional
    .test('mobile-format', 'Contact Number should be a 10-digit number', function(value) {
      if (!value || value.length === 0) return true; // Allow empty values
      return /^\d{10}$/.test(value);
    }),
  appointment: Yup.boolean(),
  appointment_date: Yup.date()
    .nullable()
    .when("appointment", {
      is: true,
      then: (schema) => schema.required("Date is required"),
      otherwise: (schema) => schema.nullable(),
    }),
  time: Yup.string()
    .nullable()
    .when("appointment", {
      is: true,
      then: (schema) => schema.required("Appointment Time is required"),
      otherwise: (schema) => schema.nullable(),
    }),
  doctor: Yup.string()
    .nullable()
    .when("appointment", {
      is: true,
      then: (schema) => schema.required("Assigning a doctor is required"),
      otherwise: (schema) => schema.nullable(),
    }),
  indicator: Yup.string()
    .nullable()
    .when("appointment", {
      is: true,
      then: (schema) => schema.required("Invoice type / service is required"),
      otherwise: (schema) => schema.nullable(),
    }),
  department_id: Yup.number()
    .nullable()
    .when("appointment", {
      is: true,
      then: (schema) => schema.min(0).required("Department is required"),
      otherwise: (schema) => schema.nullable(),
  }),
  payment_mode: Yup.object()
    .nullable()
    .when("appointment", {
      is: true,
      then: (schema) => schema.required("Payment mode is required"),
      otherwise: (schema) => schema.nullable(),
    }),
});

const EMPTY_PATIENT_INFO = {};

const AddPatientModal = ({ open, onClose, patient_info: patientInfoFromParent = EMPTY_PATIENT_INFO }) => {
  const navigate = useNavigate();
  const now = new Date();
  const { user } = useAuth();
  const [startNewPatientFlow, setStartNewPatientFlow] = useState(false);
  const patient_info = startNewPatientFlow ? EMPTY_PATIENT_INFO : patientInfoFromParent;
  const existingPatientId = patient_info?.id ?? patient_info?.patientId ?? null;
  const hasExistingPatient = existingPatientId != null && existingPatientId !== "";
  const [allowRegistrationForExistingPatient, setAllowRegistrationForExistingPatient] = useState(false);
  /** Existing patient with no OPD appointment and no REG/CONSULT invoice yet. */
  const [isFirstOpdBillingVisitPatient, setIsFirstOpdBillingVisitPatient] = useState(false);
  /** Success step: only show Print Invoice when an invoice was created in this flow. */
  const [canPrintInvoice, setCanPrintInvoice] = useState(false);
  const [current_step, setCurrentStep] = useState({
    new_patient: true,
    create_invoice: false,
    success: false,
  });
  const location = useLocation();
  const [department_list, setDepartmentList] = useState([]);
  const [staff_list, setStaffList] = useState([]);
  const [token_list, setTokenList] = useState([]);
  const [doctor_list, setDoctorList] = useState([]);
  const [doctor, setDoctor] = useState({});
  const [wardList, setWardList] = useState([]);
  const [bedList, setBedList] = useState([]);
  const [existingAppointments, setExistingAppointments] = useState<AppointmentDTO[]>([]);
  const [latestToken, setLatestToken] = useState<string>("");
  const gstPercentage = useOrganizationGstPercentage(user, { enabled: open });
  const consultationWaiverSettings = useOpdConsultationWaiverSettings(user, {
    enabled: open,
  });
  const [invoiceAttachmentFile, setInvoiceAttachmentFile] = useState<File | null>(null);
  const [vitalContext, setVitalContext] = useState<{
    patientId: string | number | null;
    appointmentId: string | number | null;
  }>({ patientId: null, appointmentId: null });
  const [portalCredentials, setPortalCredentials] = useState<{
    username?: string;
    password?: string;
  } | null>(null);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    existingAppointment: any;
    patientId: string | number | null;
    doctorId: string | number | null;
    appointmentDateTime: string | null;
  }>({
    open: false,
    existingAppointment: null,
    patientId: null,
    doctorId: null,
    appointmentDateTime: null,
  });
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [rescheduleSaving, setRescheduleSaving] = useState(false);
  /** User chose Create New beside a pending same-day appointment; do not delete or block. */
  const [allowAdditionalSameDayAppointment, setAllowAdditionalSameDayAppointment] = useState(false);
  /** When set, Create Invoice step bills an existing appointment (no second createAppointment). */
  const [invoiceOnlyAppointmentId, setInvoiceOnlyAppointmentId] = useState<
    string | number | null
  >(null);
  const prevOpenRef = useRef(false);
  const currentStepRef = useRef(current_step);
  currentStepRef.current = current_step;
  const rescheduleTargetRef = useRef(null);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setCurrentStep({
        new_patient: true,
        create_invoice: false,
        success: false,
      });
      setInvoiceOnlyAppointmentId(null);
      setIsRescheduling(false);
      setRescheduleSaving(false);
      setAllowAdditionalSameDayAppointment(false);
      rescheduleTargetRef.current = null;
      setCanPrintInvoice(false);
      setStartNewPatientFlow(false);
      setVitalContext({ patientId: null, appointmentId: null });
      setConfirmationDialog({
        open: false,
        existingAppointment: null,
        patientId: null,
        doctorId: null,
        appointmentDateTime: null,
      });
    }
    prevOpenRef.current = open;
  }, [open]);

  const goToSuccessWithVitals = (
    patientId: string | number | null | undefined,
    appointmentId: string | number | null | undefined,
    options: { hasInvoice?: boolean } = {}
  ) => {
    setCanPrintInvoice(Boolean(options.hasInvoice));
    setVitalContext({
      patientId: patientId ?? null,
      appointmentId: appointmentId ?? null,
    });
    setCurrentStep({
      new_patient: false,
      create_invoice: false,
      success: true,
    });
    // Notify the front-desk table to reload so the new appointment appears immediately.
    notifyOpdPatientsRefresh();
  };

  const capturePortalCredentials = (newPatient: any) => {
    const credentials = newPatient?.portalCredentials;
    if (!credentials) return;
    setPortalCredentials({
      username: credentials.username,
      password: credentials.password,
    });
  };

  useEffect(() => {
    fetchDepartments();
    fetchInitialData();
    (async () => {
      try {
        const tokens = await getAllTokens();
        if (Array.isArray(tokens) && tokens.length > 0) {
          // Show latest token for today only (daily sequence starts at 1)
          const todaysTokens = tokens.filter((t: any) =>
            shouldAutoGenerateToken(
              t.appointmentDateTime ||
                t.appointment_date_time ||
                t.createdAt ||
                t.created_at
            )
          );
          if (todaysTokens.length === 0) {
            setLatestToken("");
            return;
          }
          const sorted = [...todaysTokens].sort((a: any, b: any) => {
            const aNum = Number(extractTokenNumber(a) ?? 0);
            const bNum = Number(extractTokenNumber(b) ?? 0);
            return bNum - aNum;
          });
          const val = extractTokenNumber(sorted[0]);
          setLatestToken(val != null ? String(val) : "");
        }
      } catch (e) {
        // ignore errors fetching tokens
      }
    })();
  }, [patient_info]);

  useEffect(() => {
    let isMounted = true;

    const checkRegistrationEligibility = async () => {
      // New patients: REGISTRATION only.
      // Existing with no OPD history (no REG/CONSULT invoice, no appointment): REGISTRATION only.
      // Existing old patients: CONSULTATION only; payment rules use org waiver settings.
      if (!hasExistingPatient) {
        if (isMounted) {
          setAllowRegistrationForExistingPatient(false);
          setIsFirstOpdBillingVisitPatient(false);
        }
        return;
      }

      if (isMounted) {
        setAllowRegistrationForExistingPatient(false);
        setIsFirstOpdBillingVisitPatient(false);
      }

      try {
        const [invoices, appointments] = await Promise.all([
          getInvoicesByPatientId(existingPatientId),
          getAppointmentsByPatientId(existingPatientId),
        ]);
        const firstOpdVisit = isFirstOpdBillingVisit({ invoices, appointments });
        const latestWaiverAnchorDate = getLatestOpdConsultationWaiverAnchorDate(
          invoices,
          patient_info
        );

        const requiresPayment = requiresConsultationPaymentAfterVisitGap(
          latestWaiverAnchorDate,
          consultationWaiverSettings
        );

        if (isMounted) {
          setIsFirstOpdBillingVisitPatient(firstOpdVisit);
          setAllowRegistrationForExistingPatient(requiresPayment);
        }
      } catch (error) {
        if (isMounted) {
          setAllowRegistrationForExistingPatient(
            requiresConsultationPaymentAfterVisitGap(null, consultationWaiverSettings)
          );
          setIsFirstOpdBillingVisitPatient(false);
        }
      }
    };

    checkRegistrationEligibility();

    return () => {
      isMounted = false;
    };
  }, [
    hasExistingPatient,
    existingPatientId,
    consultationWaiverSettings.enabled,
    consultationWaiverSettings.gapDays,
  ]);

  const appointmentInvoiceTypeOptions = useMemo(
    () =>
      getAppointmentInvoiceTypeOptions({
        hasExistingPatient,
        isFirstOpdBillingVisit: isFirstOpdBillingVisitPatient,
      }),
    [hasExistingPatient, isFirstOpdBillingVisitPatient]
  );

  const appointmentInvoiceContext = useMemo(
    () => ({
      hasExistingPatient,
      isFirstOpdBillingVisit: isFirstOpdBillingVisitPatient,
      allowRegistrationAfterGap: allowRegistrationForExistingPatient,
    }),
    [
      hasExistingPatient,
      isFirstOpdBillingVisitPatient,
      allowRegistrationForExistingPatient,
    ]
  );

  const validationSchema = useMemo(
    () =>
      baseAppointmentValidationSchema.shape({
        amount: createAppointmentAmountValidationSchema(appointmentInvoiceContext),
      }),
    [appointmentInvoiceContext]
  );

  const fetchDoctorsByDepartment = async (department_id: string) => {
    setDoctorList([]);
    try {
      // Use the new API that searches in multiple department fields
      const doctors = await getDoctorsByDepartmentIdMultiple(department_id);
      console.log("Fetched doctors with multiple departments:", doctors);
      setDoctorList(doctors);
    } catch (error) {
      console.log("Error fetching doctors:", error);
      // Fallback to the old API if the new one fails
      try {
        const fallbackDoctors = await getDoctorByDepartmentId(department_id);
        console.log("Fallback doctors:", fallbackDoctors);
        setDoctorList(fallbackDoctors);
      } catch (fallbackError) {
        console.log("Fallback also failed:", fallbackError);
        setDoctorList([]);
      }
    }
  };

    const fetchInitialData = async () => {
      try {
        const [wards, staff] = await Promise.all([
          getAllWards(),
          getAllStaff(),
        ]);
        setWardList(wards);
        setStaffList(staff);
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };
  
  const fetchBedsForWard = async (wardId) => {
    try {
      const beds = await getBedsByWard(wardId);

      // 🔥 Filter only unoccupied beds (isOccupied === false)
      const availableBeds = beds.filter(bed => bed.isOccupied === false);

      setBedList(availableBeds);
    } catch (error) {
      console.error("Failed to fetch beds", error);
      setBedList([]);
    }
  };

  const fetchDoctorsById = async (doctor_id: string) => {
    setDoctor({});
    try {
      const data = await getDoctorById(doctor_id);
      setDoctor(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchAllStaff();
  }, []);

  const fetchAllStaff = async () => {
    console.log("fetchAllStaff called");
    setDoctor({}); // reset selected doctor

    try {
      const data = await getAllStaff();
      console.log("Staff fetched:", data); 
      setStaffList(data);
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    }
  };

  const fetchDepartments = async () => {
    setDepartmentList([]);
    try {
      const data = await getAllDepartment();
      setDepartmentList(data);
    } catch (error) {
      console.log(error);
    }
  };
  
  const fetchExistingAppointments = async (doctorId: string, appointmentDate: dayjs.Dayjs) => {
    try {
      console.log("Fetching appointments for Doctor ID:", doctorId, "on Date:", appointmentDate.format("YYYY-MM-DD"));
      const appointments = await getAppointmentsByDoctorAndDate(doctorId, appointmentDate.format("YYYY-MM-DD"));
      console.log("existing appointment is :- ", appointments);
      setExistingAppointments(appointments);
    } catch (error) {
      console.error("Error fetching existing appointments:", error);
    }
  };

  // Check for existing appointment for same patient, doctor, and date
  const checkExistingAppointment = async (
    patientId: string | number,
    doctorId: string | number,
    appointmentDate: dayjs.Dayjs,
    selectedTime?: string | null
  ) => {
    try {
      if (allowAdditionalSameDayAppointment) {
        return null;
      }
      if (!patientId || !doctorId) {
        console.log("[checkExistingAppointment] Missing patientId or doctorId:", { patientId, doctorId });
        return null;
      }
      
      console.log("[checkExistingAppointment] Checking for existing appointment:", {
        patientId,
        doctorId,
        appointmentDate: appointmentDate.format("YYYY-MM-DD")
      });
      
      // Get all appointments for this patient
      const patientAppointments = await getAppointmentsByPatientId(patientId);
      
      console.log("[checkExistingAppointment] Patient appointments found:", patientAppointments?.length || 0);
      
      if (!Array.isArray(patientAppointments) || patientAppointments.length === 0) {
        console.log("[checkExistingAppointment] No appointments found for patient");
        return null;
      }
      
      // Only pending/active visits block a new booking. Completed visits stay as history.
      const appointmentDateStr = appointmentDate.format("YYYY-MM-DD");
      const matchingPending = patientAppointments.filter((apt: any) => {
        const aptDate = apt.appointmentDateTime 
          ? dayjs(apt.appointmentDateTime).format("YYYY-MM-DD")
          : (apt.appointmentDate ? dayjs(apt.appointmentDate).format("YYYY-MM-DD") : null);
        
        const aptDoctorId = apt.doctorId || apt.doctor_id || apt.doctor?.id || null;
        
        const dateMatch = aptDate === appointmentDateStr;
        const doctorMatch = Number(aptDoctorId) === Number(doctorId);
        const isActiveConflict = isBlockingSameDayAppointment(apt);
        
        console.log("[checkExistingAppointment] Comparing appointment:", {
          aptId: apt.id ?? apt.appointmentId,
          aptDate,
          appointmentDateStr,
          dateMatch,
          aptDoctorId,
          doctorId,
          doctorMatch,
          isCancel: apt.isCancel,
          status: apt.status || apt.appointmentStatus,
          tokenStatus: apt.tokenStatus || apt.token_status,
          isActiveConflict,
          matches: dateMatch && doctorMatch && isActiveConflict
        });
        
        return dateMatch && doctorMatch && isActiveConflict;
      });

      const selectedTimeHHmm = selectedTime
        ? dayjs(selectedTime, ["h:mm A"]).format("HH:mm")
        : null;
      const existingAppt =
        (selectedTimeHHmm
          ? matchingPending.find((apt: any) => {
              const aptTime = apt.appointmentDateTime
                ? dayjs(apt.appointmentDateTime).format("HH:mm")
                : null;
              return aptTime === selectedTimeHHmm;
            })
          : null) || matchingPending[0] || null;
      
      if (existingAppt) {
        console.log("[checkExistingAppointment] Found existing appointment:", existingAppt);
      } else {
        console.log("[checkExistingAppointment] No matching appointment found");
      }
      
      return existingAppt || null;
    } catch (error) {
      console.error("[checkExistingAppointment] Error checking existing appointment:", error);
      return null;
    }
  };

  /** Validate the appointment step and move to invoice. Appointment is created only on invoice submit. */
  const handleContinueToInvoiceStep = async () => {
    const values = formik.values;
    const fieldsToValidate = [
      "name",
      "gender",
      "dob",
      "address",
      "appointment_date",
      "time",
      "doctor",
      "department_id",
    ];

    formik.setTouched({
      name: true,
      gender: true,
      dob: true,
      address: true,
      aadhar: true,
      email: true,
      mobile: true,
      appointment_date: true,
      time: true,
      doctor: true,
      department_id: true,
    });

    const validationErrors = await formik.validateForm();
    const appointmentStepErrors = Object.keys(validationErrors).filter((key) => {
      if (!fieldsToValidate.includes(key)) return false;
      if (key === "aadhar" && (!values.aadhar || values.aadhar === "")) return false;
      if (key === "mobile" && (!values.mobile || values.mobile === "")) return false;
      return true;
    });

    if (appointmentStepErrors.length > 0) {
      console.log("Validation errors:", appointmentStepErrors);
      return;
    }

    if (!values.doctor || !values.appointment_date || !values.time) {
      toast.error("Please fill in doctor, date, and time before continuing.", {
        position: "bottom-center",
        autoClose: 3000,
      });
      return;
    }

    const formattedTime = dayjs(values.time, ["h:mm A"]).format("HH:mm");
    const appointmentDateTime = `${dayjs(values.appointment_date).format("YYYY-MM-DD")}T${formattedTime}`;

    // Skip conflict check when billing an appointment that already exists (reschedule).
    if (!invoiceOnlyAppointmentId && !isRescheduling && hasExistingPatient && patient_info?.id) {
      const patientId = patient_info.id;
      const existingAppt = await checkExistingAppointment(
        patientId,
        values.doctor,
        dayjs(values.appointment_date),
        values.time
      );

      if (existingAppt) {
        setConfirmationDialog({
          open: true,
          existingAppointment: existingAppt,
          patientId,
          doctorId: values.doctor,
          appointmentDateTime,
        });
        return;
      }
    }

    setCurrentStep({
      new_patient: false,
      create_invoice: true,
      success: false,
    });
  };

const fetchExistingPatients = async () => {
    try {
      const patients = await getAllPatients(); // Fetch all patients
      return patients; // Return the list of patients
    } catch (error) {
      console.error("Error fetching existing patients:", error);
      return []; // Return an empty array on error
    }
  };

  // Function to check for duplicates
  const checkForDuplicates = async (values) => {
    const existingPatients = await fetchExistingPatients(); // Fetch existing patients from your API
    return existingPatients.some(patient => 
      patient.name === values.name &&
      patient.dob === dayjs(values.dob).format("YYYY-MM-DD") &&
      patient.aadhar === values.aadhar
    );
  };

  const validateFieldUniqueness = async (values) => {
    try {
      const patients = await getAllPatients(); // fetch all patients
      const errors = {};

      patients.forEach((patient) => {
        // Skip current patient if editing
        if (patient_info?.id && patient.id === patient_info.id) return;

        // Aadhar check
        if (
          values.aadhar &&
          patient.aadhar &&
          String(patient.aadhar).trim() === String(values.aadhar).trim()
        ) {
          errors.aadhar = "Aadhar already exists.";
        }

        // Mobile check
        if (
          values.mobile &&
          patient.mobile &&
          String(patient.mobile).trim() === String(values.mobile).trim()
        ) {
          errors.mobile = "Mobile number already exists.";
        }

        // Email check
        if (
          values.email &&
          patient.email &&
          patient.email.toLowerCase().trim() === values.email.toLowerCase().trim()
        ) {
          errors.email = "Email already exists.";
        }
      });

      return errors;
    } catch (error) {
      console.error("Error validating uniqueness", error);
      return {};
    }
  };

  // Function to proceed with appointment creation (extracted for reuse)
  const proceedWithAppointmentCreation = async (
    patientId: string | number,
    doctorId: string | number,
    appointmentDateTime: string,
    values: any,
    patient_req_body: any,
    options: { existingAppointmentId?: string | number | null } = {}
  ) => {
    try {
      const selectedDepartment = department_list.find(
        (dept) => String(dept?.id) === String(values.department_id)
      );
      const selectedDepartmentName =
        selectedDepartment?.name || selectedDepartment?.departmentName || null;

      // Validate everything needed for invoice BEFORE creating appointment,
      // so we never leave appointment-without-invoice orphans (e.g. patient 1819).
      const paymentModeValue = values.payment_mode as any;
      if (!paymentModeValue || !paymentModeValue.mode) {
        toast.error("Please select a payment mode to create the appointment", {
          position: "bottom-center",
          autoClose: 3000,
        });
        formik.setFieldTouched("payment_mode", true);
        formik.setFieldError("payment_mode", "Payment mode is required");
        return;
      }
      if (
        paymentModeValue.mode === "credit" &&
        !paymentModeValue.organizationId
      ) {
        toast.error("Please select a credit organization", {
          position: "bottom-center",
          autoClose: 3000,
        });
        formik.setFieldTouched("payment_mode", true);
        formik.setFieldError("payment_mode", "Credit organization is required");
        return;
      }

      const subtotal = Number(values.amount) || 0;
      const amountError = validateAppointmentInvoiceAmount({
        ...appointmentInvoiceContext,
        amount: subtotal,
        indicator: values.indicator,
      });
      if (amountError) {
        toast.error(amountError, {
          position: "bottom-center",
          autoClose: 3000,
        });
        formik.setFieldTouched("amount", true);
        formik.setFieldError("amount", amountError);
        return;
      }

      // Upload attachment before appointment so upload failures never skip invoice.
      let attachmentUrl: string | null = null;
      if (invoiceAttachmentFile instanceof File) {
        try {
          const uploaded = await uploadAttachment(invoiceAttachmentFile);
          attachmentUrl = resolveAttachmentUrl(uploaded);
        } catch (uploadErr) {
          console.error("Invoice attachment upload failed; continuing without attachment:", uploadErr);
          toast.warning("Attachment upload failed. Creating invoice without attachment.", {
            position: "bottom-center",
            autoClose: 4000,
          });
        }
      }

      let createdAppt: any = null;
      if (options.existingAppointmentId) {
        // Reschedule / invoice-only path: appointment already exists
        createdAppt = {
          id: options.existingAppointmentId,
          appointmentId: options.existingAppointmentId,
          appointmentDateTime,
        };
      } else {
        // apiClient will automatically add organizationId via interceptor
        createdAppt = await createAppointment({
          patientId: patientId,
          doctorId: doctorId,
          appointmentDateTime,
        });

        if (!createdAppt || !resolveAppointmentId(createdAppt)) {
          throw new Error("Appointment creation did not return a valid appointment. Please try again.");
        }
      }

      // Keep invoice creation immediately after appointment. Do not let patient-type
      // sync / token / reminders block or abort invoice creation.
      const { sgst, cgst, totalAmount } = calcInvoiceGstBreakdown(
        subtotal,
        gstPercentage
      );

      const paymentMode = paymentModeValue?.mode || "cash";
      const orgIdForCredit =
        paymentMode === "credit" ? paymentModeValue?.organizationId || null : null;

      let hospitalOrgId = getOrganizationId(user);
      if (!hospitalOrgId) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            const orgId = parsedUser?.organizationId;
            if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
              hospitalOrgId = Number(orgId);
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
      }

      const hospitalOrganizationName = getOrganizationName(user) || "Aryan Hospital";
      const patientName = values.name || patient_info?.name || "";
      const createdByUserId = user?.id || null;
      const appointmentId = resolveAppointmentId(createdAppt);

      const newInvoice = {
        patientId: patientId,
        doctorId: doctorId != null && doctorId !== "" ? Number(doctorId) : undefined,
        appointmentId: appointmentId != null ? Number(appointmentId) : undefined,
        departmentId:
          values.department_id != null && values.department_id !== ""
            ? Number(values.department_id)
            : undefined,
        amount: subtotal,
        sgst,
        cgst,
        totalAmount,
        date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        invoiceType: values.indicator,
        paymentMode: paymentMode.toUpperCase(),
        organizationId: hospitalOrgId,
        creditOrganizationId: orgIdForCredit,
        billType: "OPD",
        fromName: hospitalOrganizationName,
        toName: patientName,
        userId: createdByUserId,
        particulars: (values as any).particulars?.trim?.() || "",
        ...(attachmentUrl ? { attachment: attachmentUrl } : {}),
      };

      let invoice: any = null;
      let invoiceError: string | null = null;
      try {
        invoice = await createInvoice(newInvoice);
      } catch (firstInvErr: any) {
        console.error("Invoice creation failed; retrying once:", firstInvErr);
        try {
          // Retry without attachment in case attachment payload caused the failure
          const { attachment, ...invoiceWithoutAttachment } = newInvoice as any;
          invoice = await createInvoice(invoiceWithoutAttachment);
        } catch (invErr: any) {
          invoiceError =
            invErr?.response?.data?.message ||
            invErr?.response?.data ||
            invErr?.message ||
            "Invoice creation failed";
          console.error(
            "Invoice creation failed after appointment was created:",
            invErr
          );
        }
      }

      if (invoice?.id) {
        rememberInvoiceDepartment(
          hospitalOrgId,
          {
            patientId,
            appointmentId,
            invoiceId: invoice.id,
          },
          {
            departmentId: values.department_id,
            departmentName: selectedDepartmentName,
            doctorId,
          }
        );
      }

      // Non-critical follow-ups — never abort invoice/success because of these
      try {
        await syncPatientTypeForWorkflow(patientId, "OUT", { force: true });
      } catch (syncErr) {
        console.error("syncPatientTypeForWorkflow failed after appointment/invoice:", syncErr);
      }

      if (createdAppt?.id || createdAppt?.appointmentId) {
        scheduleRemindersForAppointment(
          {
            ...createdAppt,
            appointmentDateTime,
            patientId,
            doctorId,
            isVirtual: false,
          },
          {
            patient: patient_info || { id: patientId, mobile: values.mobile, name: values.name },
            doctor,
          }
        ).catch((error) => {
          console.error("Failed to schedule appointment reminders:", error);
        });
      }

      const createdApptDateTime =
        createdAppt?.appointmentDateTime ||
        createdAppt?.appointment_date_time ||
        createdAppt?.appointmentDate ||
        appointmentDateTime;
      if (shouldAutoGenerateToken(createdApptDateTime)) {
        try {
          const gen = await generateTokenForAppointment(
            createdAppt?.id || createdAppt?.appointmentId,
            { ...createdAppt, appointmentDateTime: createdApptDateTime }
          );
          const tokenVal = extractTokenNumber(gen);
          if (tokenVal != null) setLatestToken(String(tokenVal));
        } catch (error) {
          console.error("Failed to generate token for appointment:", error);
          const fallback = extractTokenNumber(createdAppt);
          if (fallback != null) {
            setLatestToken(String(fallback));
          }
        }
      } else {
        setLatestToken("");
      }

      if (invoiceError) {
        toast.error(
          `Appointment created but invoice failed: ${invoiceError}. Please create the invoice manually for this patient.`,
          {
            position: "bottom-center",
            autoClose: 6000,
          }
        );
      }

      setInvoiceOnlyAppointmentId(null);
      goToSuccessWithVitals(
        patientId,
        createdAppt?.id || createdAppt?.appointmentId,
        { hasInvoice: !!invoice?.id }
      );
    } catch (error) {
      console.error("Error in proceedWithAppointmentCreation:", error);
      throw error;
    }
  };

  // Enter reschedule mode. Time slot only selects a new time; Update saves it.
  const handleRescheduleAppointment = () => {
    const { existingAppointment } = confirmationDialog;
    const targetAppointment = rescheduleTargetRef.current || existingAppointment;
    const targetAppointmentId = resolveAppointmentId(targetAppointment);

    if (!targetAppointmentId) {
      toast.error("Cannot reschedule: Appointment ID is missing");
      setConfirmationDialog({ ...confirmationDialog, open: false });
      return;
    }

    if (isAppointmentCompleted(targetAppointment)) {
      toast.error("Completed appointments cannot be rescheduled. Please create a new appointment.");
      setConfirmationDialog({ ...confirmationDialog, open: false });
      setIsRescheduling(false);
      rescheduleTargetRef.current = null;
      return;
    }

    rescheduleTargetRef.current = targetAppointment;
    setIsRescheduling(true);
    setConfirmationDialog({ ...confirmationDialog, open: false });
    if (!formik.values.time) {
      toast.info("Please select a time slot, then click Update.");
    }
  };

  const handleConfirmReschedule = async () => {
    const targetAppointment =
      rescheduleTargetRef.current || confirmationDialog.existingAppointment;
    const patientId = confirmationDialog.patientId || patient_info?.id;
    const doctorId = confirmationDialog.doctorId || formik.values.doctor;

    if (!resolveAppointmentId(targetAppointment)) {
      toast.error("Cannot reschedule: Appointment ID is missing");
      return;
    }
    if (isAppointmentCompleted(targetAppointment)) {
      toast.error("Completed appointments cannot be rescheduled. Please create a new appointment.");
      setIsRescheduling(false);
      rescheduleTargetRef.current = null;
      return;
    }
    if (!formik.values.appointment_date || !formik.values.time) {
      toast.error("Please select a date and time slot to reschedule.");
      return;
    }

    const formattedTime = dayjs(formik.values.time, ["h:mm A"]).format("HH:mm");
    const appointmentDateTime = `${dayjs(formik.values.appointment_date).format(
      "YYYY-MM-DD"
    )}T${formattedTime}`;

    try {
      setRescheduleSaving(true);
      const { id: rescheduleId, payload: reschedulePayload } =
        buildReschedulePayloadForSingleAppointment(
          targetAppointment,
          appointmentDateTime,
          { patientId, doctorId }
        );
      await updateAppointment(rescheduleId, reschedulePayload);
      try {
        await syncPatientTypeForWorkflow(patientId, "OUT", { force: true });
      } catch (syncErr) {
        console.error("syncPatientTypeForWorkflow failed after reschedule:", syncErr);
      }
      rescheduleAppointmentReminders({
        appointmentId: rescheduleId,
        appointmentDateTime,
        patientId,
        doctorId,
        isVirtual: false,
        patientMobile: patient_info?.mobile || formik.values.mobile || null,
        patientName: patient_info?.name || formik.values.name || null,
        doctorMobile: doctor?.mobile || null,
        doctorName: doctor?.name || null,
        departmentName: doctor?.departmentName || doctor?.department_name || null,
      }).catch((error) => {
        console.error("Failed to reschedule appointment reminders:", error);
      });
      toast.success("Appointment rescheduled successfully!");
      setIsRescheduling(false);
      rescheduleTargetRef.current = null;
      notifyOpdPatientsRefresh();
      await handelCloseModalState();
    } catch (error: any) {
      console.error("Error rescheduling appointment:", error);
      toast.error(
        error?.response?.data ||
          error?.message ||
          "Failed to reschedule appointment",
        { position: "bottom-center", autoClose: 3000 }
      );
    } finally {
      setRescheduleSaving(false);
    }
  };

  // Handle create new appointment — keep the existing pending visit and book another.
  const handleCreateNewAppointment = async () => {
    try {
      const { patientId, doctorId, appointmentDateTime } = confirmationDialog;
      setConfirmationDialog({ ...confirmationDialog, open: false });
      setIsRescheduling(false);
      setAllowAdditionalSameDayAppointment(true);

      if (patientId && doctorId && appointmentDateTime) {
        if (currentStepRef.current.create_invoice) {
          await proceedWithAppointmentCreation(
            patientId,
            doctorId,
            appointmentDateTime,
            formik.values,
            { id: patientId, name: formik.values.name }
          );
        } else {
          setCurrentStep({
            new_patient: false,
            create_invoice: true,
            success: false,
          });
        }
      }
    } catch (error) {
      console.error("Error in handleCreateNewAppointment:", error);
      toast.error("Failed to create new appointment");
    }
  };

  const formik = useFormik({
    initialValues: {
      name: patient_info?.["name"] || "",
      address: patient_info?.["address"] || "",
      aadhar: patient_info?.["aadhar"] || "",
      email: patient_info?.["email"] || "",
      dob: patient_info?.["dob"] ? dayjs(patient_info?.["dob"]) : null,
      mobile: patient_info?.["mobile"] || "",
      gender: patient_info?.["gender"] || "",
      appointment: hasExistingPatient,
      appointment_date: null,
      time: "",
      doctor: "",
      indicator: hasExistingPatient ? "CONSULTATION" : "REGISTRATION",
      amount: 0,
      attachment: "",
      particulars: "",
      department_id: "",
      patient_type: "OUT",
      ward_id: "",
      bed_id: "",
      nurse_id: "",
      in_doctor_id: "",
      admission_amount: "",
      payment_mode: { mode: "cash" }
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const errors = await validateFieldUniqueness(values);

      if (Object.keys(errors).length > 0) {
        Object.entries(errors).forEach(([field, message]) => {
          formik.setFieldError(field, message);
        });
        return; // stop submission
      }

      // Handle form submission logic here
      console.log("Form values:", values);
  
      // Check for duplicates only if patient_info is not provided (i.e., new patient)
      if (!hasExistingPatient) {
        const isDuplicate = await checkForDuplicates(values);
        if (isDuplicate) {
          toast.error("A patient with the same name, DOB, or Aadhar already exists.");
          return;
        }
      }
      const patient_req_body = {
        name: values.name,
        mobile: values.mobile,
        email: values.email,
        aadhar: values.aadhar,
        address: values.address,
        dob: dayjs(values.dob).format("YYYY-MM-DD"),
        gender: values.gender,
        patientType: "OUT",
      };

      try {
        if (formik.values.appointment) {
          if (hasExistingPatient) {
            patient_req_body["id"] = patient_info?.["id"];
            if (
              !(
                patient_info?.["name"] === patient_req_body.name &&
                patient_info?.["dob"] === patient_req_body.dob &&
                patient_info?.["aadhar"] === patient_req_body.aadhar
              )
            ) {
              await updatePatient(patient_req_body["id"], patient_req_body);
            }
          } else {
            const new_patient = await registerPatient(patient_req_body);
            capturePortalCredentials(new_patient);
            patient_req_body["id"] = new_patient["id"];
          }

          if (formik.values.patient_type === "IN") {
            // Validate bed availability before saving assignment
            // Re-fetch beds to ensure we have the latest bed status
            try {
              const beds = await getBedsByWard(formik.values.ward_id);
              const selectedBed = beds.find(bed => bed.id === formik.values.bed_id);
              
              if (!selectedBed) {
                toast.error("Selected bed not found. Please select a different bed.", {
                  position: "bottom-center",
                  autoClose: 3000,
                });
                return;
              }
              
              // Check if bed is already occupied
              if (selectedBed.isOccupied === true) {
                toast.error("This bed is already occupied. Please select an available bed.", {
                  position: "bottom-center",
                  autoClose: 3000,
                });
                formik.setFieldError("bed_id", "This bed is already occupied");
                return;
              }
            } catch (bedCheckError) {
              console.error("Error checking bed availability:", bedCheckError);
              toast.error("Failed to verify bed availability. Please try again.", {
                position: "bottom-center",
                autoClose: 3000,
              });
              return;
            }
            
            const assignmentData = {
              patientId: patient_req_body["id"],
              wardId: formik.values.ward_id,
              departmentId: formik.values.department_id,
              bedId: formik.values.bed_id,
              nurseId: formik.values.nurse_id,
              doctorId: formik.values.doctor,
              admissionDate: new Date().toISOString(),
            };
            const new_assign = await saveAssignment(assignmentData);
            await syncPatientTypeForWorkflow(patient_req_body["id"], "IN");
            const admissionData = {
              patientId: patient_req_body["id"],
              admissionDate: new Date().toISOString(),
              admissionTime: new Date().toTimeString().split(' ')[0],
              status: "admit",
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              admissionAmount: formik.values.admission_amount,
            };
            await create(admissionData);
          }

          // Invoice submit: create appointment (unless billing a pre-existing/rescheduled one) then invoice.
          if (currentStepRef.current.create_invoice) {
            const formattedTime = dayjs(values.time, ["h:mm A"]).format("HH:mm");
            const appointmentDateTime = `${dayjs(values.appointment_date).format("YYYY-MM-DD")}T${formattedTime}`;
            console.log("[OutPatientAppointmentModal] Invoice step submit - creating appointment and invoice", {
              invoiceOnlyAppointmentId,
            });
            if (!invoiceOnlyAppointmentId) {
              const existingAppt = await checkExistingAppointment(
                patient_req_body["id"],
                values.doctor,
                dayjs(values.appointment_date),
                values.time
              );
              if (existingAppt) {
                setConfirmationDialog({
                  open: true,
                  existingAppointment: existingAppt,
                  patientId: patient_req_body["id"],
                  doctorId: values.doctor,
                  appointmentDateTime,
                });
                return;
              }
            }
            await proceedWithAppointmentCreation(
              patient_req_body["id"],
              values.doctor,
              appointmentDateTime,
              values,
              patient_req_body,
              { existingAppointmentId: invoiceOnlyAppointmentId }
            );
          } else if (currentStepRef.current.new_patient) {
            // Ensure we have all required values before checking
            if (!patient_req_body["id"] || !values.doctor || !values.appointment_date || !values.time) {
              console.error("[OutPatientAppointmentModal] Missing required values for appointment check:", {
                patientId: patient_req_body["id"],
                doctorId: values.doctor,
                appointmentDate: values.appointment_date,
                time: values.time
              });
              toast.error("Please fill in all required fields (patient, doctor, date, and time)", {
                position: "bottom-center",
                autoClose: 3000,
              });
              return;
            }

            const formattedTime = dayjs(values.time, ["h:mm A"]).format("HH:mm");
            const appointmentDateTime = `${dayjs(values.appointment_date).format("YYYY-MM-DD")}T${formattedTime}`;

            console.log("[OutPatientAppointmentModal] Checking for existing appointment before creating:", {
              patientId: patient_req_body["id"],
              doctorId: values.doctor,
              appointmentDate: values.appointment_date,
              appointmentDateTime
            });

            // Check for existing appointment for same patient, doctor, and date
            const existingAppt = await checkExistingAppointment(
              patient_req_body["id"],
              values.doctor,
              dayjs(values.appointment_date),
              values.time
            );

            console.log("[OutPatientAppointmentModal] Existing appointment check result:", existingAppt);

            if (existingAppt) {
              console.log("[OutPatientAppointmentModal] Existing appointment found, showing confirmation dialog");
              // Show confirmation dialog
              setConfirmationDialog({
                open: true,
                existingAppointment: existingAppt,
                patientId: patient_req_body["id"],
                doctorId: values.doctor,
                appointmentDateTime: appointmentDateTime,
              });
              console.log("[OutPatientAppointmentModal] Confirmation dialog state set:", {
                open: true,
                existingAppointment: existingAppt,
                patientId: patient_req_body["id"],
                doctorId: values.doctor,
                appointmentDateTime: appointmentDateTime,
              });
              return; // Stop submission, wait for user decision
            }

            console.log("[OutPatientAppointmentModal] No existing appointment found, proceeding with creation");
            // No existing appointment, proceed with creating new appointment
            await proceedWithAppointmentCreation(
              patient_req_body["id"],
              values.doctor,
              appointmentDateTime,
              values,
              patient_req_body
            );
          }
        } else {
          if (!!patient_info) {
            patient_req_body["id"] = patient_info?.["id"];
            await updatePatient(patient_req_body["id"], patient_req_body);
            handelCloseModalState();
          } else {
            const new_patient = await registerPatient(patient_req_body);
            capturePortalCredentials(new_patient);

            if (formik.values.patient_type === "IN") {
              // Validate bed availability before saving assignment
              // Re-fetch beds to ensure we have the latest bed status
              try {
                const beds = await getBedsByWard(formik.values.ward_id);
                const selectedBed = beds.find(bed => bed.id === formik.values.bed_id);
                
                if (!selectedBed) {
                  toast.error("Selected bed not found. Please select a different bed.", {
                    position: "bottom-center",
                    autoClose: 3000,
                  });
                  return;
                }
                
                // Check if bed is already occupied
                if (selectedBed.isOccupied === true) {
                  toast.error("This bed is already occupied. Please select an available bed.", {
                    position: "bottom-center",
                    autoClose: 3000,
                  });
                  formik.setFieldError("bed_id", "This bed is already occupied");
                  return;
                }
              } catch (bedCheckError) {
                console.error("Error checking bed availability:", bedCheckError);
                toast.error("Failed to verify bed availability. Please try again.", {
                  position: "bottom-center",
                  autoClose: 3000,
                });
                return;
              }
              
              const admissionData = {
                patientId: new_patient["id"],
                admissionDate: new Date().toISOString(),
                admissionTime: new Date().toTimeString().split(' ')[0],
                status: "admit",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                admissionAmount: formik.values.admission_amount,
              };
              const new_admission = await create(admissionData);

              const assignmentData = {
                patientId: new_patient["id"],
                admissionId: new_admission.id,
                departmentId: formik.values.department_id,
                wardId: formik.values.ward_id,
                bedId: formik.values.bed_id,
                nurseId: formik.values.nurse_id,
                doctorId: formik.values.doctor,
                admissionDate: new Date().toISOString(),
              };
              await saveAssignment(assignmentData);
              await syncPatientTypeForWorkflow(new_patient["id"], "IN");
            }

            if (!!new_patient["id"]) {
              goToSuccessWithVitals(new_patient["id"], null, { hasInvoice: false });
            }
          }
        }
      } catch (error) {
        toast.error(error?.response?.data || "Something went wrong", {
          position: "bottom-center",
          autoClose: 3000,
        });
        console.error(error);
      }
    }
  });

  const handelCloseModalState = async () => {
    if (location.pathname === "/manage-patient") {
      navigate("/manage-patient");
    }
    if (location.pathname === "/path-patient") {
      navigate("/path-patient");
    }
    if (location.pathname === "/front-desk") {
      navigate("/front-desk");
    }
    await onClose();
    formik.resetForm();
    setInvoiceAttachmentFile(null);
    setDoctorList([]);
    setVitalContext({ patientId: null, appointmentId: null });
    setPortalCredentials(null);
    setCanPrintInvoice(false);
    setInvoiceOnlyAppointmentId(null);
    setIsRescheduling(false);
    setStartNewPatientFlow(false);
    setAllowAdditionalSameDayAppointment(false);
    rescheduleTargetRef.current = null;
    setCurrentStep({
      new_patient: true,
      create_invoice: false,
      success: false,
    });
  };

  // Function to get the current time in HH:mm format
  const getCurrentTime = () => {
    return dayjs().format("HH:mm");
  };

  useEffect(() => {

  console.log("Received patient_info:", patient_info); // Log the incoming patient_info
    // Reset form values when patient_info changes
    formik.setValues({
      name: patient_info?.name || "",
      address: patient_info?.address || "",
      aadhar: patient_info?.aadhar || "",
      email: patient_info?.email || "",
      dob: patient_info?.dob ? dayjs(patient_info.dob) : null,
      mobile: patient_info?.mobile || "",
      gender: patient_info?.gender || "",
      appointment: hasExistingPatient,
      appointment_date: null,
      time: "",
      doctor: "",
      indicator: hasExistingPatient ? "CONSULTATION" : "REGISTRATION",
      amount: 0,
      attachment: "",
      particulars: "",
      department_id: "",
      patient_type: "OUT",
      ward_id: "",
      bed_id: "",
      nurse_id: "",
      in_doctor_id: "",
      admission_amount: "",
      payment_mode: { mode: "cash" },
    });
    setInvoiceAttachmentFile(null);
  }, [patient_info]);

  useEffect(() => {
    if (!formik.values.appointment) return;
    if (!appointmentInvoiceTypeOptions.includes(formik.values.indicator)) {
      formik.setFieldValue("indicator", appointmentInvoiceTypeOptions[0]);
    }
  }, [formik.values.appointment, formik.values.indicator, appointmentInvoiceTypeOptions]);

  const visibleDepartments = useMemo(
    () =>
      filterDepartmentsForOpdWorkflow(department_list, {
        keepIds: [formik.values.department_id],
      }),
    [department_list, formik.values.department_id]
  );

  const handlePrintInvoice = async () => {
    try {
      const values = formik.values;
      const subtotal = Number(values.amount) || 0;
      const { gstAmount, totalAmount } = calcInvoiceGstBreakdown(
        subtotal,
        gstPercentage
      );
      const serviceName =
        String(values.indicator || "CONSULTATION").toUpperCase() === "REGISTRATION"
          ? "Registration"
          : "Consultation";
      const visitDate = values.appointment_date
        ? dayjs(values.appointment_date).format("DD MMM YYYY")
        : dayjs().format("DD MMM YYYY");

      let selectedDoctor =
        doctor_list?.find?.(
          (d: any) => String(d?.id) === String(values.doctor)
        ) || null;
      if (
        !selectedDoctor &&
        doctor &&
        typeof doctor === "object" &&
        ((doctor as any).name ||
          (doctor as any).doctorName ||
          (doctor as any).fullName)
      ) {
        selectedDoctor = doctor;
      }
      if (!selectedDoctor && values.doctor) {
        try {
          selectedDoctor = await getDoctorById(values.doctor);
        } catch (error) {
          console.warn(
            "[OutPatientAppointmentModal] Could not load doctor for print:",
            error
          );
        }
      }
      const doctorName = String(
        (selectedDoctor as any)?.name ||
          (selectedDoctor as any)?.doctorName ||
          (selectedDoctor as any)?.fullName ||
          ""
      ).trim();

      const age =
        values.dob && dayjs(values.dob).isValid()
          ? dayjs().diff(dayjs(values.dob), "year")
          : "";

      let hospitalAddress = "";
      let hospitalPhone = "";
      const organizationId = getOrganizationId(user);
      if (organizationId) {
        try {
          const profile = await fetchOrganizationProfile(organizationId);
          hospitalAddress = profile?.address || "";
          hospitalPhone = profile?.phone || "";
        } catch (error) {
          console.warn(
            "[OutPatientAppointmentModal] Could not load organization profile for print:",
            error
          );
        }
      }

      const bodyHtml = buildOpdServiceInvoiceDocumentHtml({
        hospitalName: getOrganizationName(user) || "Aryan Hospital",
        hospitalAddress,
        hospitalPhone,
        visitDate,
        doctorName,
        patient: {
          id: vitalContext.patientId || patient_info?.id || "N/A",
          name: values.name || patient_info?.name || "Patient",
          gender: values.gender || patient_info?.gender || "",
          age,
          dob: values.dob ? dayjs(values.dob).format("YYYY-MM-DD") : "",
          mobile: values.mobile || patient_info?.mobile || "",
          address: values.address || patient_info?.address || "",
        },
        serviceName,
        amountLabel: formatInvoiceInr(subtotal),
        summaryLines: [
          { label: "Subtotal (₹)", value: formatInvoiceInr(subtotal) },
          {
            label: `GST @ ${gstPercentage}% (₹)`,
            value: formatInvoiceInr(gstAmount),
          },
          {
            label: "Total Payable Amount (₹)",
            value: formatInvoiceInr(totalAmount),
          },
        ],
        notes:
          (values as any).particulars?.trim?.() ||
          "This is a computer-generated invoice and does not require a signature.",
        appointmentId: vitalContext.appointmentId ?? "",
      });

      await printPatientSummaryDocument({
        title: `${serviceName} Invoice`,
        bodyHtml,
      });
    } catch (error) {
      console.error("[OutPatientAppointmentModal] Failed to print invoice:", error);
      toast.error("Failed to print invoice");
    }
  };

  const getModalTitle = () => {
    if (current_step.success) return "Success";
    if (current_step.create_invoice) return "Create Invoice";
    return patient_info?.id ? "Create Appointment" : "Add New Patient";
  };

  return (
    <>
    <BasicModal
      open={open}
      onClose={handelCloseModalState}
      sx={current_step.success ? SUCCESS_STEP_MODAL_SX : LARGE_FORM_MODAL_SX}
      title={getModalTitle()}
    >
      <Box display={"flex"} flexDirection={"column"}>
        {current_step.new_patient && (
          <>
            <Box>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={1.5}>
                  {/* // Conditionally show IN-patient assignment form: */}

                  {formik.values.patient_type === "IN" && (
                    <>
                      <Grid item xs={12}>
                        <h3 style={{ marginBottom: 16 }}>Patient Assignments</h3>
                      </Grid>

                      <Grid item xs={6}>
                        <TextField
                          id="departmentId"
                          name="department_id"
                          fullWidth
                          select
                          label="Department"
                          value={formik.values.department_id}
                          onChange={async (e) => {
                            formik.setFieldValue("department_id", e.target.value);
                            formik.setFieldValue("doctor", "");
                            formik.setFieldValue("appointment_date", null);
                            formik.setFieldValue("time", "");
                            setDoctor({});
                            await fetchDoctorsByDepartment(e.target.value);
                            formik.validateField("department_id");
                          }}
                          error={
                            formik.touched.department_id &&
                            Boolean(formik.errors.department_id)
                          }
                          helperText={
                            formik.touched.department_id && formik.errors.department_id
                          }
                        >
                          {visibleDepartments.map((department_id) => (
                            <MenuItem key={department_id["id"]} value={department_id["id"]}>
                              {department_id["name"]}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={6}>
                        <TextField
                          select
                          fullWidth
                          name="doctor"
                          label="Assign Doctor"
                          value={formik.values.doctor}
                          onChange={async (e) => {
                            formik.setFieldValue("doctor", e.target.value);
                            await fetchDoctorsById(e.target.value);
                            if (formik.values.appointment_date) {
                              fetchExistingAppointments(e.target.value, formik.values.appointment_date);
                            }
                            formik.validateField("doctor");
                          }}
                          onBlur={formik.handleBlur}
                          error={formik.touched.doctor && Boolean(formik.errors.doctor)}
                          helperText={
                            doctor_list.length === 0
                              ? "No doctors available for this department"
                              : formik.touched.doctor && formik.errors.doctor
                          }
                          disabled={doctor_list.length === 0}
                          SelectProps={{
                            MenuProps: {
                              PaperProps: {
                                style: {
                                  maxHeight: 200,
                                  overflowY: "auto",
                                },
                              },
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                              getContentAnchorEl: null,
                            },
                          }}
                        >
                        {(doctor_list || []).map((doctor) => (
                          <MenuItem key={doctor.id} value={doctor.id}>
                            Dr. {doctor.name}
                          </MenuItem>
                        ))} 
                        </TextField>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <TextField
                          select
                          fullWidth
                          name="ward_id"
                          label="Ward"
                          value={formik.values.ward_id}
                          onChange={(e) => {
                            const ward_id = e.target.value;
                            formik.setFieldValue("ward_id", ward_id);
                            formik.setFieldTouched("ward_id", true);
                            fetchBedsForWard(ward_id);
                          }}
                          onBlur={formik.handleBlur}
                          error={formik.touched.ward_id && Boolean(formik.errors.ward_id)}
                          helperText={formik.touched.ward_id && formik.errors.ward_id}
                        >
                          {wardList.map((ward) => (
                            <MenuItem key={ward.id} value={ward.id}>
                              {ward.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          select
                          fullWidth
                          name="bed_id"
                          label="Bed"
                          value={formik.values.bed_id}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.bed_id && Boolean(formik.errors.bed_id)}
                          helperText={formik.touched.bed_id && formik.errors.bed_id}
                        >
                          {bedList.length === 0 ? (
                            <MenuItem disabled>No beds available</MenuItem>
                          ) : (
                            bedList.map((bed) => (
                              <MenuItem key={bed.id} value={bed.id}>
                                {bed.bedNumber}
                              </MenuItem>
                            ))
                          )}
                        </TextField>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          id="nurse_id"
                          name="nurse_id"
                          fullWidth
                          select
                          label="Assign Nurse"
                          value={formik.values.nurse_id}
                          onChange={formik.handleChange}
                        >
                          {staff_list
                            .filter((user) => user.userRole.toLowerCase() === "staff")
                            .map((nurse) => (
                              <MenuItem key={nurse.id} value={nurse.id}>
                                {nurse.name}
                              </MenuItem>
                            ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          id="admission_amount"
                          name="admission_amount"
                          label="Admission Amount"
                          value={formik.values.admission_amount}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.admission_amount && Boolean(formik.errors.admission_amount)
                          }
                          helperText={
                            formik.touched.admission_amount && formik.errors.admission_amount
                          }
                        />
                      </Grid>
                    </>
                  )}

                  {formik.values.appointment && (
                    <>
                      {/* Department Select */}
                      <Grid item xs={6}>
                        <TextField
                          id="department"
                          name="department"
                          fullWidth
                          select
                          label="Department"
                          value={formik.values.department_id}
                          onChange={async (e) => {
                            formik.setFieldValue("department_id", e.target.value);
                            formik.setFieldValue("doctor", "");
                            formik.setFieldValue("appointment_date", null);
                            formik.setFieldValue("time", "");
                            setDoctor({});
                            await fetchDoctorsByDepartment(e.target.value);
                            formik.validateField("department_id");
                          }}
                          error={formik.touched.department_id && Boolean(formik.errors.department_id)}
                          helperText={formik.touched.department_id && formik.errors.department_id}
                        >
                          {visibleDepartments.map((department_id) => (
                            <MenuItem key={department_id.id} value={department_id.id}>
                              {department_id.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      {/* Doctor Select */}
                      <Grid item xs={6}>
                        <TextField
                          id="doctor"
                          name="doctor"
                          fullWidth
                          select
                          label="Assign Doctor"
                          value={formik.values.doctor}
                          onChange={async (e) => {
                            formik.setFieldValue("doctor", e.target.value);
                            await fetchDoctorsById(e.target.value);

                            if (formik.values.appointment_date) {
                              fetchExistingAppointments(e.target.value, formik.values.appointment_date);
                            }

                            formik.validateField("doctor");
                          }}
                          error={formik.touched.doctor && Boolean(formik.errors.doctor)}
                          helperText={formik.touched.doctor && formik.errors.doctor}
                        >
                          {/* {doctor_list.map((doctor) => (
                            <MenuItem key={doctor.id} value={doctor.id}>
                              {doctor.name}
                            </MenuItem>
                          ))} */}
                             {Array.isArray(doctor_list) && doctor_list.map((doctor) => (
                              <MenuItem key={doctor.id} value={doctor.id}>
                                {doctor.name}
                              </MenuItem>
                            ))}
                        </TextField>
                      </Grid>

                      {/* Date Picker */}
                      <Grid item xs={12}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Appointment Date"
                            format="YYYY-MM-DD"
                            value={formik.values.appointment_date}
                            onChange={async (value) => {
                              formik.setFieldValue("time", "");
                              formik.setFieldValue("appointment_date", value);
                              if (formik.values.doctor) {
                                fetchExistingAppointments(formik.values.doctor, value ?? dayjs());
                                
                                // Check for existing appointment when both doctor and date are selected
                                // Only check if we have a patient ID (for existing patients)
                                if (patient_info?.id && value && !isRescheduling) {
                                  const existingAppt = await checkExistingAppointment(
                                    patient_info.id,
                                    formik.values.doctor,
                                    dayjs(value)
                                  );
                                  
                                  if (existingAppt) {
                                    console.log("[OutPatientAppointmentModal] Existing appointment found on date/doctor selection");
                                    // Show confirmation dialog - we'll store the date but not the time yet
                                    setConfirmationDialog({
                                      open: true,
                                      existingAppointment: existingAppt,
                                      patientId: patient_info.id,
                                      doctorId: formik.values.doctor,
                                      appointmentDateTime: null, // Will be set when time is selected
                                    });
                                  }
                                }
                              }
                              formik.validateField("appointment_date");
                            }}
                            minDate={dayjs()}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: formik.touched.appointment_date && Boolean(formik.errors.appointment_date),
                                helperText: formik.touched.appointment_date && formik.errors.appointment_date,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      {/* Time Slots */}
                      <Grid item xs={12} gap={2} display="flex" flexWrap="wrap">
                        {(() => {
                          // Check if confirmation dialog is open - block time selection
                          if (confirmationDialog.open) {
                            return (
                              <Alert severity="warning" sx={{ width: "100%" }}>
                                Please resolve the existing appointment conflict above before selecting a time.
                              </Alert>
                            );
                          }
                          
                          // Check if date is selected first
                          if (!formik.values.appointment_date) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                Please select date
                              </Typography>
                            );
                          }

                          // Check if doctor is selected after department is selected
                          if (formik.values.department_id && !formik.values.doctor) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                Please select doctor
                              </Typography>
                            );
                          }

                          const selectedDay = dayjs(formik.values.appointment_date).format("dddd").toLowerCase();
                          const schedule = doctor?.visitingSchedules?.[selectedDay];

                          if (!schedule || !Array.isArray(schedule.timings) || schedule.timings.length === 0) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                No available time slots for this day.
                              </Typography>
                            );
                          }

                            const currentTime = getCurrentTime(); // Get the current time
                            const selectedDate = dayjs(formik.values.appointment_date);
                            const isToday = selectedDate.isSame(dayjs(), 'day'); // Check if the selected date is today

                          return schedule.timings.map((time, index) => {
                            // const selectedDate = dayjs(formik.values.appointment_date);
                            // const now = dayjs();

                            const slotTime = dayjs(`${selectedDate.format("YYYY-MM-DD")} ${time}`, "YYYY-MM-DD hh:mm A");
                            // const isToday = selectedDate.isSame(now, 'day');
                            //const isPast = isToday && slotTime.isBefore(now);
                            const isBooked = existingAppointments.some(appointment =>
                              isBlockingSameDayAppointment(appointment) &&
                              dayjs(appointment.appointmentDateTime).format("HH:mm") === slotTime.format("HH:mm")
                            );
                          //   const isBooked = existingAppointments.some(appointment =>
                          //   dayjs(appointment.appointmentDateTime).format("HH:mm") === dayjs(time, "h:mm A").format("HH:mm")
                          // );
                            //const isDisabled = isPast || isBooked;
                            // const isDisabled = isBooked;
                            // const isDisabled = dayjs(time, "h:mm A").isBefore(dayjs(currentTime, "HH:mm")); // Disable if before current time
                            // Disable if the selected date is today and the slot time is before the current time
                              const isDisabled = isToday && slotTime.isBefore(dayjs());
                            //console.log("Slot:", time, "| Past:", isPast, "| Booked:", isBooked, "| Disabled:", isDisabled);

                            return (
                              <Box key={index}>
                                {/* <Button
                                  variant={formik.values.time === time ? "contained" : "outlined"}
                                  onClick={() => !isDisabled && formik.setFieldValue("time", time)}
                                  sx={{
                                    minWidth: 72,
                                    mb: 1,
                                    backgroundColor: isDisabled ? '#e0e0e0' : 'default',
                                    color: isDisabled ? 'white' : 'default',
                                    '&:hover': {
                                      backgroundColor: isDisabled ? '#bdbdbd' : 'default',
                                    },
                                  }}
                                  disabled={isDisabled}
                                >
                                  {time}
                                </Button> */}

                                <Button
                                variant={formik.values.time === time ? "contained" : "outlined"}
                                onClick={() => {
                                  if (isBooked || isDisabled) return;
                                  formik.setFieldValue("time", time);
                                }}
                                sx={{
                                  minWidth: 72,
                                  mb: 1,
                                  backgroundColor: isDisabled ? '#e0e0e0' : isBooked ? 'grey' : 'default',
                                  color: isDisabled ? 'white' : isBooked ? 'white' : 'default',
                                  '&:hover': {
                                    backgroundColor: isDisabled ? '#bdbdbd' : isBooked ? 'darkgrey' : 'default',
                                  },
                                }}
                                disabled={isBooked || isDisabled} // Disable the button if the time is already booked or before current time
                              >
                                {time}
                              </Button>

                              </Box>
                            );
                          });
                        })()}
                      </Grid>

                      {/* Error Message for Time */}
                      <Grid item xs={12} mt={0} pt={0}>
                        {doctor.visitingSchedules?.[
                          dayjs(formik.values.appointment_date).format("dddd").toLowerCase()
                        ]?.timings?.length > 0 &&
                          formik.touched.time &&
                          formik.errors.time && (
                            <Typography color="error" variant="caption">
                              {formik.errors.time}
                            </Typography>
                          )}
                      </Grid>
                    </>
                  )}

                  {!formik.values.appointment ? (
                    <Grid item xs={12}>
                      <FormModalActions
                        onCancel={handelCloseModalState}
                        submitType="submit"
                        loading={formik.isSubmitting}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <FormModalActions
                        onCancel={handelCloseModalState}
                        submitLabel={isRescheduling ? "Update" : "Continue"}
                        submitType="button"
                        submitDisabled={isRescheduling ? !formik.values.time : false}
                        loading={isRescheduling ? rescheduleSaving : false}
                        onSubmit={
                          isRescheduling
                            ? handleConfirmReschedule
                            : handleContinueToInvoiceStep
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              </form>
            </Box>
          </>
        )}

        {current_step.create_invoice && (
          <>
            <Box>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={1.5}>
                  {/* Confirm Your Details at the top */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ pt: 0.5, pb: 0.5 }}>
                      Confirm Your Details
                    </Typography>
                    <Box sx={FORM_CONFIRM_DETAILS_BOX_SX}>
                      <Box display={"flex"} justifyContent={"space-between"}>
                        <Box display={"flex"} flexDirection={"column"}>
                          <Typography fontWeight={"bold"}>
                            Doctor Details
                          </Typography>
                          <Typography>{doctor["name"]}</Typography>
                          <Typography>{doctor["departmentName"]}</Typography>
                        </Box>

                        <Box display={"flex"} flexDirection={"column"}>
                          <Typography fontWeight={"bold"}>
                            Patient Details
                          </Typography>
                          <Typography>{formik.values.name}</Typography>
                          <Typography>{formik.values.email}</Typography>
                          <Typography>{formik.values.mobile}</Typography>
                        </Box>

                        <Box display={"flex"} flexDirection={"column"}>
                          <Typography fontWeight={"bold"}>
                            Appointment Details
                          </Typography>
                          <Typography>
                            {dayjs(formik.values.appointment_date).format("D MMM")}, {formik.values.time}
                          </Typography>
                          {current_step.success && (
                            <>
                              <Typography fontWeight="medium" color="text.secondary">
                                Token Number
                              </Typography>
                              <Typography>
                                {latestToken ? latestToken : "-"}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Divider />
                  </Grid>

                  {/* Invoice fields — same layout as Create Invoice */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      id="indicator"
                      name="indicator"
                      fullWidth
                      select
                      label="Invoice Type"
                      value={formik.values.indicator}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.indicator &&
                        Boolean(formik.errors.indicator)
                      }
                      helperText={
                        formik.touched.indicator && formik.errors.indicator
                      }
                      {...DENSE_FIELD_PROPS}
                    >
                      {appointmentInvoiceTypeOptions.map((indicator) => (
                        <MenuItem key={indicator} value={indicator}>
                          {indicator}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="amount"
                      name="amount"
                      label="Amount (₹)"
                      type="number"
                      value={formik.values.amount}
                      onChange={(e) => {
                        const amount = Number(e.target.value) || 0;
                        formik.setFieldValue("amount", amount);
                      }}
                      error={
                        formik.touched.amount && Boolean(formik.errors.amount)
                      }
                      helperText={formik.touched.amount && formik.errors.amount}
                      inputProps={{ min: 0, step: 0.01 }}
                      {...DENSE_FIELD_PROPS}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <InvoiceSummarySection
                      subtotal={formik.values.amount || 0}
                      gstPercentage={gstPercentage}
                      paymentModeValue={formik.values.payment_mode}
                      onPaymentModeChange={(value) =>
                        formik.setFieldValue("payment_mode", value)
                      }
                      paymentError={
                        formik.touched.payment_mode &&
                        Boolean(formik.errors.payment_mode)
                      }
                      paymentHelperText={
                        (formik.touched.payment_mode &&
                          (formik.errors.payment_mode as any)) ||
                        ""
                      }
                      attachmentField={
                        <InvoiceAttachmentControl
                          attachmentName={(formik.values as any).attachment}
                          attachmentFile={invoiceAttachmentFile}
                          onFileChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setInvoiceAttachmentFile(file);
                            formik.setFieldValue("attachment", file.name);
                          }}
                          onDelete={() => {
                            setInvoiceAttachmentFile(null);
                            formik.setFieldValue("attachment", "");
                          }}
                        />
                      }
                      remarksName="particulars"
                      remarksValue={(formik.values as any).particulars || ""}
                      onRemarksChange={formik.handleChange}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormModalActions
                      onCancel={() =>
                        setCurrentStep({
                          ...current_step,
                          new_patient: true,
                          create_invoice: false,
                        })
                      }
                      cancelLabel="Back"
                      submitType="submit"
                      submitLabel="Create Invoice"
                      submitDisabled={isAppointmentInvoiceSubmitDisabled({
                        amount: formik.values.amount,
                        indicator: formik.values.indicator,
                        paymentMode: formik.values.payment_mode as any,
                        ...appointmentInvoiceContext,
                      })}
                      loading={formik.isSubmitting}
                    />
                  </Grid>
                </Grid>
              </form>
            </Box>
          </>
        )}

        {current_step.success && (
          <PatientRegistrationSuccessStep
            showSuccessMessage={Boolean(
              formik.values.appointment && latestToken
            )}
            successMessage={
              formik.values.appointment && latestToken
                ? `Token Number: ${latestToken}`
                : ""
            }
            patientId={vitalContext.patientId}
            appointmentId={vitalContext.appointmentId}
            portalCredentials={portalCredentials}
            hideDismissActions
            enableVitals={
              Boolean(vitalContext.appointmentId) ||
              Boolean(formik.values.appointment)
            }
            onVitalsSaved={handelCloseModalState}
            onPrintInvoice={canPrintInvoice ? handlePrintInvoice : null}
            onRegisterNew={() => {
              setStartNewPatientFlow(true);
              setVitalContext({ patientId: null, appointmentId: null });
              setPortalCredentials(null);
              setInvoiceAttachmentFile(null);
              setCanPrintInvoice(false);
              setInvoiceOnlyAppointmentId(null);
              setIsRescheduling(false);
              setAllowAdditionalSameDayAppointment(false);
              rescheduleTargetRef.current = null;
              setDoctor({});
              setExistingAppointments([]);
              setAllowRegistrationForExistingPatient(false);
              setIsFirstOpdBillingVisitPatient(false);
              setCurrentStep({
                ...current_step,
                new_patient: true,
                create_invoice: false,
                success: false,
              });
              formik.resetForm({
                values: {
                  name: "",
                  address: "",
                  aadhar: "",
                  email: "",
                  dob: null,
                  mobile: "",
                  gender: "",
                  appointment: false,
                  appointment_date: null,
                  time: "",
                  doctor: "",
                  indicator: "REGISTRATION",
                  amount: 0,
                  attachment: "",
                  particulars: "",
                  department_id: "",
                  patient_type: "OUT",
                  ward_id: "",
                  bed_id: "",
                  nurse_id: "",
                  in_doctor_id: "",
                  admission_amount: "",
                  payment_mode: { mode: "cash" },
                },
              });
            }}
            onClose={handelCloseModalState}
          />
        )}
      </Box>
    </BasicModal>

    {/* Confirmation Dialog for Existing Appointment - Outside BasicModal to avoid z-index issues */}
    <Dialog
      open={confirmationDialog.open}
      onClose={() => setConfirmationDialog({ ...confirmationDialog, open: false })}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="confirmation-dialog-title">
        Existing Appointment Found
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="confirmation-dialog-description">
          This patient already has a pending appointment with the same doctor on{" "}
          {confirmationDialog.existingAppointment?.appointmentDateTime
            ? dayjs(confirmationDialog.existingAppointment.appointmentDateTime).format("DD MMM YYYY, hh:mm A")
            : dayjs(formik.values.appointment_date).format("DD MMM YYYY")}
          .
          <br />
          <br />
          Would you like to:
          <br />
          • <strong>Reschedule</strong> - Change the time of this pending appointment (select a slot, then click Update)
          <br />
          • <strong>Create New</strong> - Keep the existing appointment and create another one
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", gap: 2, px: 2, pb: 2 }}>
        <Button
          onClick={() => setConfirmationDialog({ ...confirmationDialog, open: false })}
          variant="outlined"
          color="primary"
          sx={FORM_CANCEL_BUTTON_SX}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateNewAppointment}
          color="primary"
          variant="outlined"
          sx={FORM_CANCEL_BUTTON_SX}
        >
          Create New Appointment
        </Button>
        <Button
          onClick={handleRescheduleAppointment}
          color="primary"
          variant="contained"
          sx={FORM_SUBMIT_BUTTON_SX}
        >
          Reschedule
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default AddPatientModal;